import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { toastLabelsReady } from "@/components/BulkLabelReadyTable";
import { API, getAuthHeader } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, RefreshCw, FileCheck2, ShieldAlert, X } from "lucide-react";
import { SlipCheckResult } from "@/components/SlipCheckResult";

// Opens when an invoice is marked Completed. Stays open until the right
// packing slip is uploaded; then the Packing List is generated automatically.
export const InvoiceSlipCheckDialog = ({ invoiceNo, isAdmin, deferred, onDone }) => {
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  const [override, setOverride] = useState("");
  const inputRef = useRef(null);
  const [plExists, setPlExists] = useState(false);
  const [regen, setRegen] = useState(null);
  useEffect(() => { setFile(null); setRes(null); setOverride(""); }, [invoiceNo]);
  useEffect(() => {
    setPlExists(false); setRegen(null);
    if (!invoiceNo) return;
    axios.get(`${API}/packing-list/exists`, { params: { invoice_no: invoiceNo }, headers: getAuthHeader() })
      .then(r => { setPlExists(!!r.data.exists && !r.data.regenerate); if (r.data.regenerate) setRegen(r.data.changes || []); }).catch(() => {});
  }, [invoiceNo]);

  // Closing (✕ / Esc / click outside) parks the invoice in "Pending slips" — no password, audited.
  const doDefer = async () => {
    if (deferred) { onDone(); return; }
    if (busy) return;
    setBusy("defer");
    try {
      await axios.post(`${API}/packing-list/slip-check/${encodeURIComponent(invoiceNo)}/defer`, {}, { headers: getAuthHeader() });
      toast.success(`Slip upload for ${invoiceNo} moved to "Pending slips" — upload it later from there`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not close"); }
    setBusy("");
  };

  const check = async (f = file) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("invoice_no", invoiceNo);
    fd.append("packing_slip", f);
    setBusy("check");
    try {
      const r = await axios.post(`${API}/packing-list/slip-check`, fd, { headers: getAuthHeader(), timeout: 120000 });
      setRes(r.data);
    } catch (e) { setRes(null); toast.error(e.response?.data?.detail || "Could not check this slip"); }
    setBusy("");
  };

  const pick = (f) => { if (f) { setFile(f); check(f); } };

  const link = async (fileName, slipName) => {
    try {
      await axios.post(`${API}/packing-list/name-aliases`, { file_name: fileName, slip_name: slipName }, { headers: getAuthHeader() });
      toast.success(`Linked "${fileName}" → "${slipName}"`);
      check();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the link"); }
  };

  const attach = async (rowIds) => {
    try {
      const r = await axios.post(`${API}/packing-list/slip-check/attach-rows`, { invoice_no: invoiceNo, row_ids: rowIds }, { headers: getAuthHeader() });
      toast.success(`${r.data.attached} register row(s) attached to ${invoiceNo} — rechecking`);
      window.dispatchEvent(new CustomEvent("order-register:invoice-changed", { detail: { invoice_no: invoiceNo } }));
      check();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not attach the rows"); }
  };

  const unlink = async (a) => {
    try {
      await axios.delete(`${API}/packing-list/name-aliases/${a.id}`, { headers: getAuthHeader() });
      toast.success(`Removed link "${a.file_name}" → "${a.slip_name}" — rechecking`);
      check();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove the link"); }
  };

  const finalize = async (withOverride = false) => {
    const fd = new FormData();
    fd.append("invoice_no", invoiceNo);
    fd.append("packing_slip", file);
    if (withOverride) fd.append("override_reason", override);
    setBusy("final");
    try {
      const r = await axios.post(`${API}/packing-list/slip-finalize`, fd, { headers: getAuthHeader(), timeout: 180000 });
      if (r.data.requires_replacement)
        toast.warning(`Packing List made — invoice ${invoiceNo} already exists in Database A. Confirm the replacement in Packing List history.`, { duration: 9000 });
      else toast.success(`Packing List generated for ${invoiceNo} — find it in Packing List history`);
      if (r.data.bulk_data?.pending) toast.info(`Bulk Data, CN22 labels and invoice/EDF/PB for ${invoiceNo} are being made — the bell tells you when ready`, { duration: 7000 });
      else if (r.data.bulk_data?.ok) toast.success(`Bulk Data also generated — ${r.data.bulk_data.filename || invoiceNo} is ready for labels`);
      if (r.data.bulk_data?.auto_label) {
        localStorage.setItem(`auto-label-seen-${r.data.bulk_data.auto_label.id}`, "1");
        toastLabelsReady(r.data.bulk_data.auto_label, invoiceNo);
      }
      else if (r.data.bulk_data && !r.data.bulk_data.pending) toast.warning(`Bulk Data not auto-generated: ${r.data.bulk_data.error || "make it from the Bulk Data panel"}`, { duration: 8000 });
      window.dispatchEvent(new CustomEvent("order-register:invoice-changed", { detail: { invoice_no: invoiceNo } }));
      onDone();
    } catch (e) {
      if (e.response?.status === 409) { toast.info(e.response.data?.detail); window.dispatchEvent(new CustomEvent("order-register:invoice-changed", { detail: { invoice_no: invoiceNo } })); }
      else toast.error(e.response?.data?.detail || "Packing List generation failed");
    }
    setBusy("");
  };

  const closeLater = (e) => { e?.preventDefault?.(); doDefer(); };

  return (
    <Dialog open={!!invoiceNo} onOpenChange={(o) => { if (!o) doDefer(); }}>
      <DialogContent className="max-w-3xl [&>button]:hidden" onEscapeKeyDown={closeLater} onInteractOutside={closeLater} data-testid="slip-check-dialog">
        <div className="absolute right-3 top-3"><button type="button" onClick={doDefer} disabled={busy === "defer"}
          className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          title="Close — the invoice moves to Pending slips; upload the slip later from there" data-testid="slip-check-close">
          {busy === "defer" ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
        </button></div>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileCheck2 className="w-5 h-5 text-emerald-600" /> {regen ? "Regenerate packing slip" : "Packing slip check"} — {invoiceNo}
            {regen && <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="slip-check-regen-badge">CORRECTION</span>}</DialogTitle>
          <DialogDescription data-testid="slip-check-desc">
            {regen
              ? "This invoice was changed after it was completed. Upload the corrected packing slip — a new Packing List and Bulk Data are made and marked CORRECTED; the old files stay in history as Replaced."
              : plExists
              ? "Packing list already generated — compare slip vs order to confirm quantities (OK / Override)."
              : "Invoice completed. Upload its packing slip: the register parcels are compared with the slip, and the Packing List is created automatically once they match."}
          </DialogDescription>
          {regen?.length > 0 && (
            <ul className="mt-1 max-h-28 list-disc overflow-y-auto rounded border border-amber-200 bg-amber-50 py-1 pl-6 pr-2 text-xs text-amber-900" data-testid="slip-check-regen-changes">
              {regen.map((c, i) => <li key={i}>{c}</li>)}</ul>)}
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" data-testid="slip-check-input"
            onChange={e => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={!!busy} data-testid="slip-check-upload">
            <Upload className="w-4 h-4 mr-1" /> {file ? "Upload corrected slip" : "Upload packing slip"}
          </Button>
          {file && (
            <Button size="sm" variant="outline" onClick={() => check()} disabled={!!busy} data-testid="slip-check-recheck"
              title="Run the comparison again (e.g. after fixing register rows)">
              <RefreshCw className="w-4 h-4 mr-1" /> Recheck
            </Button>)}
          {file && <span className="text-xs text-slate-500 truncate max-w-[260px]" data-testid="slip-check-file">{file.name}</span>}
          {busy === "check" && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
        </div>
        <SlipCheckResult res={res} onLink={link} onAttach={attach} onUnlink={unlink} invoiceNo={invoiceNo} />
        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
          {isAdmin && res && !res.ok && (
            <div className="flex items-center gap-2 mr-auto">
              <Input value={override} onChange={e => setOverride(e.target.value)} placeholder="Admin override reason"
                className="h-8 w-56 text-xs" data-testid="slip-check-override-reason" />
              <Button size="sm" variant="outline" className="border-rose-400 text-rose-700" disabled={!override.trim() || !!busy}
                onClick={() => finalize(true)} data-testid="slip-check-override">
                <ShieldAlert className="w-4 h-4 mr-1" /> {plExists ? "Override" : "Override & generate"}
              </Button>
            </div>)}
          <Button onClick={() => finalize(false)} disabled={!res?.ok || !!busy} className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="slip-check-generate">
            {busy === "final" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileCheck2 className="w-4 h-4 mr-1" />}
            {plExists ? "OK — confirm quantities" : "Generate Packing List"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
